import ProductsActionCreators from "@/Actions/Products";
import { checkCanMultiplePricingPlansFor } from "@/Components/Utils";
import {
	ProductFormValues,
	ProductType,
	ProductPricingPlan,
	ProductBillingType,
	EditOrCloneProductDrawerMode,
	AddOrEditProductRequest,
	AddOrEditBundleRequest,
	BundleProduct,
	Product,
	PricingRadioOptions,
	PricingOptions,
	ProductMiscEnums,
	Distributor,
	BundleSettingDetail
} from "@/Contracts/Products";
import ZMessage from "@molecules/ZMessage";
import useDispatch from "@/Components/Utils/useDispatch";
import { CustomField } from "@/Contracts/Settings";
import useUpdateTemplateModal from "./useUpdateTemplateModal";
import { EProductDrawerState } from "@pages/Drawers/Products/Product/interface";
import { isProductFromEtilize } from "@/V2Utils/Products";
import { processCustomFieldFormValues } from "@/Utils/common";

const useEditOrCloneProduct = (
	productPricingPlanList: ProductPricingPlan[],
	bundledProductList: BundleProduct[],
	productImageList: string[],
	customFieldList: CustomField[],
	currentProduct: Product | null,
	showIndividualProductPricing: boolean,
	drawerState: EProductDrawerState,
	selectedDistributor: PricingRadioOptions,
	selectedPricing: ProductPricingPlan | PricingOptions | null,
	mode?: EditOrCloneProductDrawerMode | null,
	onClose?: () => void,
	bundleSettingDetail?: BundleSettingDetail,
	isBundleEnhancementEnable?: boolean,
	isAmazonDistributorEnabled?: boolean
): Record<string, any> => {
	const dispatch = useDispatch();

	const { setEditedProduct, showModal, TemplateModal } =
		useUpdateTemplateModal(currentProduct, drawerState, onClose);

	const customPricingOptions: PricingRadioOptions[] = [
		PricingOptions.Catalog,
		PricingOptions.Custom
	];

	const validSelectedDistributor = customPricingOptions.includes(
		selectedDistributor
	)
		? null
		: selectedDistributor;

	const onEditOrCloneSubmit = (
		editOrCloneProductFormValues: ProductFormValues
	) => {
		const canHaveMultiplePricingPlans = checkCanMultiplePricingPlansFor(
			editOrCloneProductFormValues.type
		);
		if (canHaveMultiplePricingPlans && !productPricingPlanList?.length) {
			ZMessage.error("Please add atleast 1 pricing plan");
			return;
		}
		if (
			editOrCloneProductFormValues.type === ProductType.Bundle &&
			!bundledProductList?.length
		) {
			ZMessage.error("Please add atleast 1 product to bundle");
			return;
		}

		const pricingPlanNamesArray = productPricingPlanList
			.filter(plan => !plan?.amazon_business_pricing_details)
			.map(plan => plan.name);

		const isPricingPlanDuplicate =
			new Set(pricingPlanNamesArray).size !==
			pricingPlanNamesArray.length;

		if (isPricingPlanDuplicate) {
			ZMessage.error("Pricing plan names has to be unique");
			return;
		}

		const isEtilizeHardwareProduct =
			isProductFromEtilize(currentProduct) &&
			editOrCloneProductFormValues.type === ProductType.Hardware;

		// new code
		if (!canHaveMultiplePricingPlans || isEtilizeHardwareProduct) {
			const resetBundlePricing =
				editOrCloneProductFormValues.type === ProductType.Bundle &&
				showIndividualProductPricing;
			if (resetBundlePricing) {
				const productPricingPlan = new ProductPricingPlan();
				productPricingPlan.setup_price =
					editOrCloneProductFormValues.selected_setup_price || 0;
				editOrCloneProductFormValues.pricing = [productPricingPlan];
			} else if (!resetBundlePricing || !validSelectedDistributor) {
				// for hardware or bundled items with parent level pricing
				const productPricingPlan = new ProductPricingPlan();
				const defaultPricingPlan = currentProduct?.pricing[0];
				productPricingPlan.id = defaultPricingPlan?.id || null;
				productPricingPlan.item_id =
					defaultPricingPlan?.item_id || null;
				productPricingPlan.session_id =
					defaultPricingPlan?.session_id || null;
				productPricingPlan.name =
					defaultPricingPlan?.name || ProductMiscEnums.DefaultPricing;
				productPricingPlan.description =
					defaultPricingPlan?.description || "";
				productPricingPlan.billing_period =
					editOrCloneProductFormValues.selected_billing_period ??
					editOrCloneProductFormValues.billing_period ??
					ProductBillingType.OneTime;
				productPricingPlan.cost_price =
					editOrCloneProductFormValues.selected_cost_price || 0;
				productPricingPlan.sell_price =
					editOrCloneProductFormValues.sell_price || 0;
				productPricingPlan.setup_price =
					editOrCloneProductFormValues.selected_setup_price || 0;
				productPricingPlan.vendor_name =
					editOrCloneProductFormValues.vendor_name;
				productPricingPlan.vendor_sku_no =
					editOrCloneProductFormValues.vendor_sku_no;
				productPricingPlan.supplier_id =
					editOrCloneProductFormValues.supplier_id;
				editOrCloneProductFormValues.pricing = [productPricingPlan];
			}
		} else {
			editOrCloneProductFormValues.pricing = productPricingPlanList || [];
		}
		if (editOrCloneProductFormValues.type === ProductType.Bundle) {
			editOrCloneProductFormValues.bundled_items = [
				...bundledProductList
			];
		}
		editOrCloneProductFormValues.item_images =
			productImageList || new Array<string>();

		if (mode === EditOrCloneProductDrawerMode.EDIT) {
			editProduct(editOrCloneProductFormValues);
		} else if (mode === EditOrCloneProductDrawerMode.CLONE) {
			cloneProduct(editOrCloneProductFormValues);
		}
	};

	const editProduct = async (editProductFormValues: ProductFormValues) => {
		dispatch(ProductsActionCreators.showProductsLoader());
		try {
			if (currentProduct) {
				const { custom_field_form_values, ...editProductFormRequest } =
					editProductFormValues;

				let editProductRequest: AddOrEditProductRequest = {
					...currentProduct,
					...editProductFormRequest,
					type: editProductFormValues.type
				};
				editProductFormValues.pricing.map(pricing => {
					pricing.sell_price = Number(pricing.sell_price);
					pricing.setup_price = Number(pricing.setup_price);
					return pricing;
				});
				editProductRequest.pricing = editProductFormValues.pricing;
				editProductRequest.bundled_items = bundledProductList.map(
					bundledProduct => {
						const newBundledProduct: AddOrEditBundleRequest = {
							...bundledProduct
						};
						delete newBundledProduct.item;
						return newBundledProduct;
					}
				);
				editProductRequest.selected_pricing_distributor =
					validSelectedDistributor;
				if (
					validSelectedDistributor === Distributor.CustomDistributor
				) {
					const tempSelectedPricing =
						selectedPricing as ProductPricingPlan;
					editProductRequest.selected_custom_distributor_details = {
						custom_distributor_id:
							tempSelectedPricing.distributor_pricing_details
								?.custom_distributor_pricing_details
								?.custom_distributor.id || "",
						selected_custom_distributor_pricing_id:
							tempSelectedPricing.distributor_pricing_details
								?.custom_distributor_pricing_details
								?.custom_distributor_pricing_id || "",
						custom_fields:
							tempSelectedPricing?.distributor_pricing_details
								?.custom_distributor_pricing_details
								?.custom_fields ?? []
					};
				}
				editProductRequest.tax_category_id =
					editProductFormValues.tax_category_id;
				editProductRequest.show_individual_product_pricing =
					isBundleEnhancementEnable
						? bundleSettingDetail?.showPrice
						: showIndividualProductPricing;
				editProductRequest.should_add_setup_price_to_margin =
					editProductFormValues.should_add_setup_price_to_margin;
				editProductRequest.unit_of_measure =
					editProductFormValues.unit_of_measure;
				editProductRequest.mandatory_custom_field_check_require = true;
				const customFieldFormValues =
					editProductFormValues.custom_field_form_values;
				const updateExistingTemplateChecked =
					editProductFormValues.update_existing_templates;
				if (customFieldFormValues && customFieldList?.length > 0) {
					editProductRequest = {
						...editProductRequest,
						custom_fields: processCustomFieldFormValues(
							customFieldFormValues,
							customFieldList
						)
					};
				}

				if (isBundleEnhancementEnable) {
					editProductRequest.show_child_quantity =
						bundleSettingDetail?.showQuantity;
					editProductRequest.show_child_items_in_quote_block =
						bundleSettingDetail?.showChildItems;
				}

				if (isAmazonDistributorEnabled) {
					editProductRequest.asin = editProductFormValues.asin;
				}

				editProductRequest.should_update_templates =
					updateExistingTemplateChecked;

				const editProductResponse = await dispatch(
					ProductsActionCreators.editProduct(editProductRequest)
				);

				setEditedProduct(editProductResponse.editProduct);

				if (
					updateExistingTemplateChecked &&
					!!editProductResponse.editProduct.templates?.length
				) {
					showModal(editProductResponse.editProduct.templates);
				} else {
					dispatch(
						ProductsActionCreators.updateProductDetails(
							editProductResponse.editProduct
						)
					);
					onClose && onClose();
					ZMessage.success("Product edited successfully");
				}
			}
		} catch (error) {
			console.error(error);
		} finally {
			dispatch(ProductsActionCreators.hideProductsLoader());
		}
	};

	const cloneProduct = async (cloneProductFormValues: ProductFormValues) => {
		dispatch(ProductsActionCreators.showProductsLoader());
		try {
			if (currentProduct) {
				let cloneProductRequest = new AddOrEditProductRequest();
				cloneProductRequest.name = cloneProductFormValues.name;
				cloneProductRequest.description =
					cloneProductFormValues.description;
				cloneProductRequest.type = cloneProductFormValues.type;
				cloneProductRequest.product_category =
					cloneProductFormValues.product_category;
				cloneProductRequest.manufacturer =
					cloneProductFormValues.manufacturer;
				cloneProductRequest.manufacturer_part_no =
					cloneProductFormValues.manufacturer_part_no;
				cloneProductRequest.item_images =
					cloneProductFormValues.item_images;
				cloneProductRequest.tax_category_id =
					cloneProductFormValues.tax_category_id;
				cloneProductRequest.show_individual_product_pricing =
					isBundleEnhancementEnable
						? bundleSettingDetail?.showPrice
						: showIndividualProductPricing;
				cloneProductRequest.should_add_setup_price_to_margin =
					cloneProductFormValues.should_add_setup_price_to_margin;
				cloneProductRequest.unit_of_measure =
					cloneProductFormValues.unit_of_measure;
				cloneProductRequest.mandatory_custom_field_check_require = true;
				cloneProductRequest.selected_pricing_distributor =
					validSelectedDistributor !== "custom"
						? validSelectedDistributor
						: null;

				if (isBundleEnhancementEnable) {
					cloneProductRequest.show_child_quantity =
						bundleSettingDetail?.showQuantity;
					cloneProductRequest.show_child_items_in_quote_block =
						bundleSettingDetail?.showChildItems;
				}

				if (isAmazonDistributorEnabled) {
					cloneProductRequest.asin = cloneProductFormValues.asin;
				}

				if (
					validSelectedDistributor === Distributor.CustomDistributor
				) {
					const tempSelectedPricing =
						selectedPricing as ProductPricingPlan;
					cloneProductRequest.selected_custom_distributor_details = {
						custom_distributor_id:
							tempSelectedPricing.distributor_pricing_details
								?.custom_distributor_pricing_details
								?.custom_distributor?.id || "",
						selected_custom_distributor_pricing_id:
							tempSelectedPricing.distributor_pricing_details
								?.custom_distributor_pricing_details
								?.custom_distributor_pricing_id || "",
						custom_fields:
							tempSelectedPricing?.distributor_pricing_details
								?.custom_distributor_pricing_details
								?.custom_fields ?? []
					};
				}
				cloneProductRequest.pricing =
					cloneProductFormValues.pricing.map(pricing => {
						pricing.id = null;
						pricing.item_id = null;
						pricing.cost_price = Number(pricing.cost_price || 0);
						pricing.sell_price = Number(pricing.sell_price || 0);
						pricing.setup_price = Number(pricing.setup_price || 0);
						pricing.margin = Number(pricing.margin || 0);
						pricing.markup = Number(pricing.markup || 0);
						return pricing;
					});
				cloneProductRequest.bundled_items =
					cloneProductFormValues.bundled_items ?? [];
				const customFieldFormValues =
					cloneProductFormValues.custom_field_form_values;
				if (customFieldFormValues && customFieldList?.length > 0) {
					cloneProductRequest = {
						...cloneProductRequest,
						custom_fields: processCustomFieldFormValues(
							customFieldFormValues,
							customFieldList
						)
					};
				}
				await dispatch(
					ProductsActionCreators.cloneProduct(cloneProductRequest)
				);
				onClose && onClose();
				ZMessage.success("Product cloned successfully");
			}
		} catch (error) {
			console.error(error);
		} finally {
			dispatch(ProductsActionCreators.hideProductsLoader());
		}
	};

	return {
		onEditOrCloneSubmit,
		TemplateModal
	};
};

export default useEditOrCloneProduct;
